import "./TwoColumnLayout.scss";

import clsx from "clsx";
import { CSSProperties, Fragment, ReactNode } from "react";

import { FCWithClassName } from "@/types";

export type TwoColumnRow = {
  left: ReactNode;
  right?: ReactNode;
};

interface Props {
  rows: TwoColumnRow[];
  id?: string;
  /**
   * Default (`false`): a real CSS grid, so `rows[i].right` shares a row with
   * `rows[i].left` and both are stretched to the taller of the two - the
   * intended "paired" look most callers want.
   *
   * `true`: column 2 packs independently instead - every `right` cell stacks
   * directly under the *previous `right` cell* (via flex, inside one grid
   * cell spanning all rows), not stretched to match column 1's row heights.
   * Use this when the two columns are unrelated tracks (e.g. Checkout's
   * summary/payment column vs. its due-now/billing-switch column) rather
   * than row-paired content. In the DOM, all `right` cells are grouped right
   * after `rows[0].left` (not after every left cell) - below `tablet`, where
   * the grid becomes a plain stacked column, that keeps the due-now-style
   * content near the top instead of pushed below unrelated left-column rows.
   */
  independentColumns?: boolean;
}

/**
 * Standalone content wrapper: two columns from `tablet` up (left = main
 * content, right = its paired aside, e.g. a "good to know" hint), one
 * stacked column only below that (mobile). Does NOT render `PortalPageLayout`
 * itself — `Portal.tsx` already wraps every routed page in one, so nesting
 * another here would double the chrome (two Sidebars, two Headers, ...).
 * Render this directly as a page's content inside `Portal`'s `<Outlet />`
 * (or anywhere else a `col-span-full` grid cell is expected).
 *
 * Pairing is positional: `rows[i].right` renders next to `rows[i].left`. A
 * row without a `right` just leaves that grid cell empty rather than
 * shifting subsequent rows - each cell gets `--row`/`--col` custom properties
 * (via inline style) that `.two-column-layout__cell` (see the companion
 * .scss) reads. Below `tablet` the container isn't a grid at all (plain
 * `flex-col`), so those grid properties are inert and the cells simply
 * stack in source order: left, right (if present), next left, ...
 */
export const TwoColumnLayout: FCWithClassName<Props> = ({
  rows,
  className,
  id,
  independentColumns = false,
}) => {
  if (independentColumns) {
    const rightCells = rows.filter((row) => row.right);

    return (
      <div
        id={id}
        className={clsx(
          "two-column-layout--independent col-span-full flex flex-col gap-y-6 tablet:grid tablet:grid-cols-2 tablet:gap-x-6 tablet:gap-y-6",
          className,
        )}
        style={{ "--row-count": rows.length } as CSSProperties}
      >
        {rows.map((row, index) => (
          <Fragment key={index}>
            <div
              style={{ "--row": index + 1, "--col": 1 } as CSSProperties}
              className="two-column-layout__cell"
            >
              {row.left}
            </div>
            {index === 0 && rightCells.length > 0 && (
              <div
                style={{ "--col": 2 } as CSSProperties}
                className="two-column-layout__cell two-column-layout__cell--spanning flex flex-col gap-y-6"
              >
                {rightCells.map((rightCell, rightIndex) => (
                  <Fragment key={rightIndex}>{rightCell.right}</Fragment>
                ))}
              </div>
            )}
          </Fragment>
        ))}
      </div>
    );
  }

  return (
    <div
      id={id}
      className={clsx(
        "col-span-full flex flex-col gap-y-6 tablet:grid tablet:grid-cols-2 tablet:gap-x-6 tablet:gap-y-6",
        className,
      )}
    >
      {rows.map((row, index) => (
        <Fragment key={index}>
          <div
            style={{ "--row": index + 1, "--col": 1 } as CSSProperties}
            className="two-column-layout__cell"
          >
            {row.left}
          </div>
          {row.right && (
            <div
              style={{ "--row": index + 1, "--col": 2 } as CSSProperties}
              className="two-column-layout__cell"
            >
              {row.right}
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
};
